/**
 * Frontend ID helpers for MySQL Int primary keys.
 * Prefer numeric comparison; coerce stringified ints safely.
 * Rejects legacy Mongo 24-char hex ids (stale localStorage / tokens).
 */

const MONGO_HEX = /^[a-fA-F0-9]{24}$/;
const POSITIVE_INT = /^\d+$/;

/** True if value looks like a legacy Mongo ObjectId hex string. */
export function isLegacyMongoHexId(value) {
  if (value == null) return false;
  const s = String(value).trim();
  return MONGO_HEX.test(s);
}

/**
 * Coerce to a positive integer id, or null if invalid / legacy hex.
 * @returns {number|null}
 */
export function normalizeId(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  if (typeof value === "bigint") {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : null;
  }
  if (typeof value === "object") {
    if (value.id != null && typeof value.id !== "object") {
      return normalizeId(value.id);
    }
    if (value._id != null && typeof value._id !== "object") {
      return normalizeId(value._id);
    }
    return null;
  }
  const s = String(value).trim();
  if (!s || MONGO_HEX.test(s) || !POSITIVE_INT.test(s)) return null;
  const n = parseInt(s, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Compare two ids as MySQL Ints (accepts numeric strings). */
export function idsMatch(a, b) {
  const x = normalizeId(a);
  const y = normalizeId(b);
  if (x == null || y == null) return false;
  return x === y;
}

/** Match scalar or array ownership field (creator / userIds) against a user id. */
export function listIncludesId(list, candidate) {
  if (list == null) return false;
  if (!Array.isArray(list)) return idsMatch(list, candidate);
  return list.some((entry) => idsMatch(entry, candidate));
}

/**
 * Value to store in creator / userIds JSON (MySQL Int).
 * Returns null if missing or legacy hex.
 */
export function toCreatorRef(id) {
  return normalizeId(id);
}

/**
 * Raw user-id candidate from an auth/result object (MySQL `id` or legacy `_id`).
 * Does not normalize — use with normalizeId / isLegacyMongoHexId.
 */
export function getRawAuthIdCandidate(resultOrProfile) {
  const result =
    resultOrProfile?.result && typeof resultOrProfile.result === "object"
      ? resultOrProfile.result
      : resultOrProfile;
  if (!result || typeof result !== "object") return undefined;
  return result.id ?? result.userId ?? result._id;
}

/**
 * Current auth user id from localStorage profile (or passed user object).
 * @returns {number|null}
 */
export function getAuthUserId(user) {
  const profile =
    user ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("profile") || "null");
      } catch {
        return null;
      }
    })();
  const raw =
    profile?.result?.id ??
    profile?.result?.userId ??
    profile?.result?._id ??
    profile?.userId ??
    profile?.id ??
    profile?._id;
  return normalizeId(raw);
}

/**
 * Normalize login/API user payload so result.id is a Number (not hex string).
 * Accepts MySQL `id` / `userId` and legacy `_id`. Leaves legacy Mongo hex untouched
 * so callers can detect and reject/clear it explicitly.
 */
export function normalizeAuthPayload(data) {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  if (next.result && typeof next.result === "object") {
    const result = { ...next.result };
    const id = normalizeId(result.id ?? result.userId ?? result._id);
    if (id != null) {
      result.id = id;
      result.userId = id;
    }
    next.result = result;
  }
  return next;
}

export default {
  isLegacyMongoHexId,
  normalizeId,
  idsMatch,
  listIncludesId,
  toCreatorRef,
  getRawAuthIdCandidate,
  getAuthUserId,
  normalizeAuthPayload,
};
